// MonthCalendar.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Row, Col, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/tr';
import './MonthCalendar.scss';
import ReminderService from '../../modules/Reminder/Services/ReminderService';
import CalendarEventService from '../../services/CalendarEventService';
import { CalendarEvent } from '../../types/CalendarEvent';
import { formatTime } from '../../utils';

dayjs.locale('tr');

const WeekDays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const MonthCalendar: React.FC = () => {
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const [reminders, setReminders] = useState<any[]>([]);

  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [calendarEventsError, setCalendarEventsError] = useState(false);
  const calendarYear = currentMonth.year();

  const firstDayOfMonth = currentMonth.startOf('month');
  const lastDayOfMonth = currentMonth.endOf('month');
  const daysInMonth = lastDayOfMonth.date();

  // ISO hafta başlangıcı Pazartesi için:
  const isoWeekday = (d: Dayjs) => (d.day() + 6) % 7;
  const leadingBlanks = isoWeekday(firstDayOfMonth);

  // Tüm hücreleri oluştur
  const cells: Array<Dayjs | null> = [];
  // Ayın başından önceki boş günler
  for (let i = 0; i < leadingBlanks; i++) {
    cells.push(null);
  }
  // O ayın 1'inden sonuna kadar
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(firstDayOfMonth.date(d));
  }
  // Son haftayı doldurmak için sonra boş hücreler
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  const prevMonth = () => setCurrentMonth(m => m.subtract(1, 'month'));
  const nextMonth = () => setCurrentMonth(m => m.add(1, 'month'));

  const today = dayjs();

  // Fetch reminders for visible month
  useEffect(() => {
    let active = true;
    setReminders([]);
    const startDate = currentMonth.startOf('month').format('YYYY-MM-DDT00:00:00');
    const endDate = currentMonth.endOf('month').format('YYYY-MM-DDT23:59:59');
    ReminderService.getAll(`?startDate=${startDate}&endDate=${endDate}`)
      .then((res: any) => {
        if (active) setReminders(res.data || []);
      })
      .catch((e: Error) => {
        console.log('Reminders fetch error:', e);
      });
    return () => { active = false; };
  }, [currentMonth]);

  useEffect(() => {
    let active = true;
    setCalendarEvents([]);
    setCalendarEventsError(false);

    const loadEvents = async () => {
      try {
        const events: CalendarEvent[] = [];
        let page = 0;
        let totalPages = 1;
        while (active && page < totalPages) {
          const response = await CalendarEventService.getAll({
            year: calendarYear, page, size: 100, sort: 'eventDate', direction: 'asc',
          });
          events.push(...response.data.content);
          totalPages = response.data.totalPages;
          page += 1;
        }
        if (active) setCalendarEvents(events);
      } catch {
        if (active) setCalendarEventsError(true);
      }
    };

    loadEvents();
    return () => { active = false; };
  }, [calendarYear]);

  // Group reminders by date string
  const remindersByDate = useMemo(() => {
    const map = new Map<string, any[]>();
    (reminders || []).forEach((r: any) => {
      const key = dayjs(r.reminderDate).format('YYYY-MM-DD');
      const arr = map.get(key) || [];
      arr.push(r);
      map.set(key, arr);
    });
    return map;
  }, [reminders]);

  const calendarEventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    calendarEvents.forEach(event => {
      const key = dayjs(event.eventDate).format('YYYY-MM-DD');
      map.set(key, [...(map.get(key) || []), event]);
    });
    return map;
  }, [calendarEvents]);

  return (
    <div className="month-calendar-container">
      {/* Başlık */}
      <Row justify="space-between" align="middle" className="mc-header">
        <Col>
          <Button aria-label="Önceki ay" onClick={prevMonth}>&lt;</Button>
        </Col>
        <Col>
          <h2>{currentMonth.format('MMMM YYYY')}</h2>
        </Col>
        <Col>
          <Button aria-label="Sonraki ay" onClick={nextMonth}>&gt;</Button>
        </Col>
      </Row>

      {calendarEventsError && (
        <Alert type="warning" role="alert" message="Genel Takvim kayıtları yüklenemedi. Lütfen daha sonra tekrar deneyin." />
      )}

      {/* Haftanın gün adları */}
      <div className="mc-weekdays">
        {WeekDays.map(d => (
          <div key={d} className="mc-weekday">{d}</div>
        ))}
      </div>

      {/* Tarih hücreleri */}
      <div className="mc-days-grid">
        {cells.map((date, idx) => {
          const isToday = date?.isSame(today, 'day');
          const dateKey = date ? date.format('YYYY-MM-DD') : '';
          const dayReminders = date ? (remindersByDate.get(dateKey) || []) : [];

          const dayEvents = date ? (calendarEventsByDate.get(dateKey) || []) : [];
          const hasEntries = dayReminders.length > 0 || dayEvents.length > 0;

          const tooltipContent = hasEntries ? (
            <div>
              {dayEvents.map(event => (
                <div key={`calendar-event-${event.id}`}>
                  <b>{formatTime(event.eventDate)}</b> - {event.name} (Genel Takvim)
                </div>
              ))}
              {dayReminders.map((r: any) => (
                <div key={`reminder-${r.id}`}>
                  <b>{formatTime(r.reminderDate)}</b> - {r.content}
                </div>
              ))}
            </div>
          ) : null;
          return (
            <Tooltip key={idx} title={tooltipContent} mouseEnterDelay={0.1}>
              <div
                aria-label={date ? `${date.format('D MMMM YYYY')}${hasEntries ? ' — Kayıt var' : ''}` : undefined}
                tabIndex={hasEntries ? 0 : undefined}
                className={
                  'mc-day-cell'
                  + (isToday ? ' today' : '')
                  + (date ? '' : ' empty')
                }
              >
                {date && (
                  <>
                    {date.date()}
                    {hasEntries && <span className="mc-dot" />}
                  </>
                )}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

export default MonthCalendar;
